import React, { useState, useEffect } from "react";
import { fetchQuestionsAndOptions, submitUserResponses, submitAssessmentOutcome, updateUserResponses, updateAssessmentOutcome } from "../../api/AssessmentHelper";
import Modal from "../Shared/Modal";
import getDietRecallScore from "../../utils/getDietRecallScore";
import QuestionRenderer from './QuestionRenderer';
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { Buffer } from "buffer"; 
import { getUserId, getAccesstoken } from "../../utils/auth";

const StartAssessment = () => {
  const [questions, setQuestions] = useState([]);
  const [options, setOptions] = useState({});
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [assessmentAdded, setAssessmentAdded] = useState(false);

  const userId = getUserId();
  
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const access_token = getAccesstoken();
  
  // Decode assessment details from URL
  const searchParams = new URLSearchParams(location.search);
  const encodedData = searchParams.get("details");
  const mode = searchParams.get("mode");

  const decodeData = (data) => {
    if (!data) return { name: "Unknown", desc: "Description not available" };
    try {
      const decoded = JSON.parse(Buffer.from(decodeURIComponent(data), "base64").toString("utf-8"));
      return decoded;
    } catch (error) {
      return { name: "Unknown", desc: "Description not available" };
    }
  };

  const { name: assessmentName } = decodeData(encodedData);

  const questionsPerPage = 10;
  const totalPages = Math.ceil(questions.length / questionsPerPage);

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!access_token || !params.assessmentId) return;

      setLoading(true);
      setError(null);

      try {
        const { questions: fetchedQuestions, options: fetchedOptions } = await fetchQuestionsAndOptions(params.assessmentId, access_token);
        setQuestions(fetchedQuestions);
        setOptions(fetchedOptions);
      } catch (error) {
        console.error('Error fetching questions:', error);
        setError("Failed to load questions.");
      } finally {
        setLoading(false);
      }
    };

    if (access_token && params.assessmentId) {
      fetchData();
    }
  }, [access_token, params.assessmentId]);

  const currentQuestions = questions.slice(
    (currentPage - 1) * questionsPerPage,
    currentPage * questionsPerPage
  );

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleResponseChange = (questionId, response) => {
    setResponses(prevResponses => ({
      ...prevResponses,
      [questionId]: response,
    }));
  };

  const calculateScore = (questionId, selectedResponse, questionType) => {
    if (questionType === "radio" || questionType === "single_choice") {
      const optionsForQuestion = options[questionId];
      const selectedOption = optionsForQuestion.find(option => option.option_label === selectedResponse);
      return selectedOption ? selectedOption.score : 0;
    }
    return 0;
  };

  const handleSubmit = async () => {
    if (!questions || questions.length === 0) {
      console.error("Questions array is undefined or empty.");
      alert("Questions data is missing. Please try again.");
      return;
    }

    const createdAt = new Date().toISOString();
    const updatedAt = new Date().toISOString();
    const textInputAssessments = ["Daily carbohydrate intake (24 hours Diet Recall)"];

    const userResponses = Object.keys(responses).map(questionId => {
      const question = questions.find(q => q.id === questionId);

      if (!question) {
        console.error(`Question not found for ID: ${questionId}`);
        return null;
      }

      const questionType = question.question_type;
      let score = 0;

      if (questionType === "text_input" && textInputAssessments.includes(assessmentName)) {
        const userInput = responses[questionId]?.trim();
        const numericValue = parseFloat(userInput);

        if (!isNaN(numericValue)) {
          score = getDietRecallScore(question.question_text.toLowerCase(), numericValue);
        } else {
          console.warn(`Invalid numeric input for question: ${question.question_text}`);
        }
      } else {
        const selectedResponse = Array.isArray(responses[questionId])
          ? responses[questionId].join(", ")
          : responses[questionId];

        score = calculateScore(questionId, selectedResponse, questionType);
      }

      return {
        user_id: userId,
        question_id: questionId,
        response: responses[questionId],
        score: score,
        created_at: createdAt,
      };
    }).filter(Boolean); // Remove any null values

    try {
      if (userResponses.length === 0) {
        alert("No valid responses to submit.");
        return;
      }

      const totalScore = userResponses.reduce((acc, curr) => {
        return acc + curr.score;
      }, 0);

      if (mode === 'edit') {
        const updatedEntries = userResponses.map(response => ({
          user_id: userId,
          question_id: response.question_id,
          response: response.response,
          score: response.score,
          updated_at: new Date().toISOString(),
        }));
        
        await updateUserResponses(params.assessmentId, updatedEntries, access_token);
        // const totalScore = userResponses.reduce((acc, curr) => acc + curr.score, 0);
        const updatedAssessmentOutcome = {
          user_id: userId,
          assessment_id: params.assessmentId,
          total_score: totalScore,
          status: "completed",
          updated_at: updatedAt,
        };
        await updateAssessmentOutcome(params.assessmentId, userId, updatedAssessmentOutcome, access_token);
      } else {
        await submitUserResponses(userResponses, access_token);
        // const totalScore = userResponses.reduce((acc, curr) => acc + curr.score, 0);
        const assessmentOutcome = {
          user_id: userId,
          assessment_id: params.assessmentId,
          total_score: totalScore,
          status: "completed",
          created_at: createdAt,
        };
        await submitAssessmentOutcome(assessmentOutcome, access_token);
      }
      navigate(`/user/assessment/responses/${params.assessmentId}?details=${encodedData}`);
      // setAssessmentAdded(true);
      // setTimeout(() => {
      //   setAssessmentAdded(false);
      //   navigate(`/user/assessment/responses/${params.assessmentId}?details=${encodedData}`);
      // }, 2000);
    //   setTimeout(() => {
    //     setAssessmentAdded(false); // Close the modal first
    
    //     const modalBackdrop = document.querySelector(".modal-backdrop");
    // if (modalBackdrop) {
    //   modalBackdrop.remove();
    // }
    
    //     setTimeout(() => {
    //       navigate(`/user/assessment/responses/${params.assessmentId}?details=${encodedData}`);
    //     }, 300); // Slight delay to ensure modal unmounts completely before navigating
    //   }, 1800);

    } catch (error) {
      alert("Failed to submit responses or assessment outcome.");
    }
  };

  const groupedQuestions = currentQuestions.reduce((groups, question) => {
    const section = question.section || '';
    if (!groups[section]) {
      groups[section] = [];
    }
    groups[section].push(question);
    return groups;
  }, {});

  if (loading) return <div>Loading questions...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="dashboard_grey_section">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="font-lg">{assessmentName} Assessment</h1>
        <Link 
          style={{ fontSize: "22px", textDecoration: "none" }} 
          onClick={handleBack}
        >
          <i className="fa-solid fa-left-long icon"></i> Back
        </Link>
      </div>

      {assessmentName === "Daily carbohydrate intake (24 hours Diet Recall)" && (
        <div className="mb-4">
          <p>1. Please select what you have eaten in last 24 hours</p>
          <p>2. Please select closest item</p>
          {/* <p>3. You may add any items other than mentioned in any other section</p> */}
        </div>
      )}

      {Object.keys(groupedQuestions).map((section, sectionIndex) => (
        <div key={sectionIndex}>
          <h3 style={{ marginTop: '20px', marginBottom: '20px', fontSize: '25px', color: 'var(--theme-color)' }}>
            {section}
          </h3>
          {groupedQuestions[section].map((question) => (
            <QuestionRenderer
              key={question.id}
              question={question}
              options={options}
              responses={responses}
              onResponseChange={handleResponseChange}
            />
          ))}
        </div>
      ))}
      <div className="d-flex justify-content-between">
        {currentPage === 1 ? (
          <button
            onClick={handleBack}
            className="btn-assessment font-xsm btns btn-animation-blue mt-xxl-4 mt-2 home-button"
          >
            Back
          </button>
        ) : (
          <button
            onClick={handlePrev}
            className="btn-assessment font-xsm btns btn-animation-blue mt-xxl-4 mt-2 home-button"
          >
            Previous
          </button>
        )}
        {currentPage < totalPages ? (
          <button
            onClick={handleNext}
            className="btn-assessment font-xsm btns btn-animation-blue mt-xxl-4 mt-2 home-button"
          >
            Next
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            className="btn-assessment font-xsm btns btn-animation-blue mt-xxl-4 mt-2 home-button"
          >
            Submit
          </button>
        )}
      </div>
      {assessmentAdded && (
        <Modal
          title="Assessment Submitted!"
          modalType="success"
          bodyText="Responses stored successfully.."
          modalId="loginModal"
        />
      )}
    </div>
  );
};

export default StartAssessment; 